import { useEffect, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

/**
 * Web-App consumer for admin campaign "overlay" channel.
 * Polls /campaign-overlay/active once per app session, shows a dismissible
 * popup, and reports seen/clicked/dismissed for the impact report.
 */
export function CampaignOverlay() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [overlay, setOverlay] = useState(null);
  const [open, setOpen] = useState(false);
  const fetchedFor = useRef(null);

  const onAdmin = location.pathname.startsWith("/app/admin");

  useEffect(() => {
    if (!user || onAdmin) return;
    if (fetchedFor.current === user.id) return;
    fetchedFor.current = user.id;
    (async () => {
      try {
        const { data } = await api.get("/campaign-overlay/active");
        if (data?.overlay) {
          setOverlay(data.overlay);
          setOpen(true);
          api.post(`/campaign-overlay/${data.overlay.message_id}/seen`).catch(() => {});
        }
      } catch { /* silent */ }
    })();
  }, [user, onAdmin]);

  const dismiss = () => {
    if (overlay) api.post(`/campaign-overlay/${overlay.message_id}/dismiss`).catch(() => {});
    setOpen(false);
  };

  const act = () => {
    if (!overlay) return;
    api.post(`/campaign-overlay/${overlay.message_id}/click`).catch(() => {});
    const link = overlay.link || "";
    if (link) {
      if (/^https?:\/\//.test(link)) {
        window.open(link, "_blank", "noopener");
      } else if (!link.startsWith("mywithin://")) {
        let path = link;
        if (path.startsWith("/(client)/")) path = "/app/" + path.slice("/(client)/".length);
        if (!path.startsWith("/")) path = "/" + path;
        navigate(path);
      }
    }
    setOpen(false);
  };

  if (!overlay) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) dismiss(); }}>
      <DialogContent className="sm:max-w-md" data-testid="campaign-overlay">
        <DialogHeader>
          <DialogTitle data-testid="campaign-overlay-title">{overlay.title}</DialogTitle>
          {overlay.body && (
            <DialogDescription className="whitespace-pre-line text-sm leading-relaxed" data-testid="campaign-overlay-body">
              {overlay.body}
            </DialogDescription>
          )}
        </DialogHeader>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="ghost" className="rounded-full" onClick={dismiss} data-testid="campaign-overlay-dismiss">
            Später
          </Button>
          {overlay.link ? (
            <Button className="rounded-full" onClick={act} data-testid="campaign-overlay-cta">
              {overlay.cta_label || "Öffnen"}
            </Button>
          ) : (
            <Button className="rounded-full" onClick={dismiss} data-testid="campaign-overlay-ok">
              {overlay.cta_label || "Verstanden"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
